import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Instagram, Twitter, Youtube, ChevronDown, X, Scissors } from "lucide-react";

interface HeaderProps {
  onLoginClick?: () => void;
  onTryFreeClick?: () => void;
}

export function Header({ onLoginClick, onTryFreeClick }: HeaderProps) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white border-b w-full">
      <nav className="w-full px-4 md:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
              R
            </div>
            <span className="font-bold text-xl">reelcat</span>
          </Link>

          {/* Tools dropdown — desktop */}
          <div className="hidden md:block relative">
            <button
              onClick={() => setToolsOpen(!toolsOpen)}
              className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-black transition-colors"
            >
              Tools
              <ChevronDown className={`w-4 h-4 transition-transform ${toolsOpen ? 'rotate-180' : ''}`} />
            </button>
            {toolsOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setToolsOpen(false)} />
                <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <Link
                    to="/tools/remove-video-background"
                    onClick={() => setToolsOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Scissors className="w-4 h-4 text-orange-500" />
                    <div>
                      <div className="font-medium">Remove Video Background</div>
                      <div className="text-xs text-gray-500">AI-powered background removal</div>
                    </div>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <a
            href="https://www.instagram.com/reelcat_official?igsh=MTR4d25tbnhwb25qYg%3D%3D&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-700 hover:text-black transition-colors"
            aria-label="Instagram"
          >
            <Instagram className="w-5 h-5" />
          </a>
          <a
            href="https://x.com/jashwanth0712?s=21"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-700 hover:text-black transition-colors"
            aria-label="X (Twitter)"
          >
            <Twitter className="w-5 h-5" />
          </a>
          <a
            href="https://youtube.com/@reelcat-app?si=YkuVGd4ux3PiejvG"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-700 hover:text-black transition-colors"
            aria-label="YouTube"
          >
            <Youtube className="w-5 h-5" />
          </a>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={onLoginClick}
            className="text-gray-700 hover:text-black font-medium text-sm"
          >
            Login
          </button>
          <button
            onClick={onTryFreeClick}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-1.5 rounded-lg font-medium text-sm"
          >
            Try For Free
          </button>
        </div>

        {/* Mobile menu button */}
        <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 py-4 space-y-3">
          <Link
            to="/tools/remove-video-background"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
          >
            <Scissors className="w-4 h-4 text-orange-500" />
            Remove Video Background
          </Link>
          <div className="flex items-center gap-4 px-3 py-2">
            <a href="https://www.instagram.com/reelcat_official?igsh=MTR4d25tbnhwb25qYg%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-black" aria-label="Instagram">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://x.com/jashwanth0712?s=21" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-black" aria-label="X (Twitter)">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="https://youtube.com/@reelcat-app?si=YkuVGd4ux3PiejvG" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-black" aria-label="YouTube">
              <Youtube className="w-5 h-5" />
            </a>
          </div>
          <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={() => { setMobileMenuOpen(false); onLoginClick?.(); }}
              className="text-left px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Login
            </button>
            <button
              onClick={() => { setMobileMenuOpen(false); onTryFreeClick?.(); }}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium text-sm text-center"
            >
              Try For Free
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
